import { useCallback, useEffect, useRef } from 'react'
import Lottie, { LottieRef } from 'lottie-react'
import styled from 'styled-components'
import checkbox from '../../../public/animations/checkbox.json'
import { TShortcutName } from '../../constants/shortcuts'
import { useKeyboardShortcut } from '../../hooks'
import { checkboxSize } from '../../styles/dimensions'
import { shouldAnimateMotion } from '../../styles/motion'
import NoStyleButton from './buttons/NoStyleButton'

const ANIM_SPEED = 1.5
const ANIM_START_FRAME = 3
const ANIM_END_FRAME = 29
const ANIM_TOTAL_FRAMES = ANIM_END_FRAME - ANIM_START_FRAME

const AnimationContainer = styled.div`
    position: absolute;
    top: -50%;
    left: -50%;
    width: ${checkboxSize.childContainer};
    height: ${checkboxSize.childContainer};
    pointer-events: none;
`

const FixedSizeButton = styled(NoStyleButton)`
    position: relative;
    min-width: ${checkboxSize.parentContainer};
    width: ${checkboxSize.parentContainer};
    height: ${checkboxSize.parentContainer};
`

interface GTCheckboxProps {
    isChecked: boolean
    /** Return false to refuse the change, so the box doesn't animate to a state that never happened. */
    onChange: (checked: boolean) => void | boolean
    disabled?: boolean
    animated?: boolean
    shortcutName?: TShortcutName
    shortcutDisabled?: boolean
}
const GTCheckbox = ({ isChecked, onChange, disabled, animated, shortcutName, shortcutDisabled }: GTCheckboxProps) => {
    const animRef: LottieRef = useRef(null)

    useEffect(() => {
        if (!isChecked) {
            animRef.current?.goToAndStop(ANIM_START_FRAME, true)
        }
    }, [isChecked])

    const onClickHandler = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
            e?.stopPropagation()
            if (onChange(!isChecked) === false) return
            if (!isChecked) {
                if (shouldAnimateMotion(Boolean(animated))) {
                    animRef.current?.play()
                } else {
                    animRef.current?.goToAndStop(ANIM_END_FRAME, true)
                }
            } else {
                animRef.current?.goToAndStop(ANIM_START_FRAME, true)
            }
        },
        [isChecked, onChange, animated]
    )

    useEffect(() => {
        if (!animRef.current) return
        animRef.current.goToAndStop(isChecked ? ANIM_TOTAL_FRAMES : ANIM_START_FRAME, true)
        animRef.current.setSpeed(ANIM_SPEED)
    }, [])

    useKeyboardShortcut(shortcutName, onClickHandler, shortcutDisabled)

    return (
        <FixedSizeButton onClick={onClickHandler} disabled={disabled}>
            <AnimationContainer>
                <Lottie
                    name="checkbox"
                    animationData={checkbox}
                    loop={false}
                    autoplay={false}
                    lottieRef={animRef}
                    initialSegment={[ANIM_START_FRAME, ANIM_END_FRAME]}
                />
            </AnimationContainer>
        </FixedSizeButton>
    )
}

export default GTCheckbox
